// client/src/services/MatchProposalService.js
import BaseService from './BaseService'
import flex from '../api/FlexFetch'
import { INACTIVE_PROPOSAL_STATUSES } from '../utils/matchStatus'

function flattenProposalPayload(result) {
  if (
    result?.success === true &&
    Object.prototype.hasOwnProperty.call(result, 'data')
  ) {
    return {
      success: true,
      ...(result.data || {}),
      ...(result.meta !== undefined ? { meta: result.meta } : {}),
    }
  }

  return result
}

function normalizeProposalError(error) {
  const payload = error?.response?.data
  const strictError = payload?.success === false ? payload.error : null

  if (!strictError || typeof strictError !== 'object') {
    return error
  }

  const normalizedError = new Error(
    strictError.message || error.message || 'Request failed',
  )
  const legacyData =
    strictError.details?.data ?? payload?.data ?? strictError.details

  normalizedError.response = {
    ...error.response,
    data: {
      ...payload,
      error: strictError.message,
      message: strictError.message,
      errorCode: strictError.code,
      ...(legacyData !== undefined ? { data: legacyData } : {}),
    },
  }

  return normalizedError
}

async function requestMatchProposal(path, options) {
  try {
    const response = await flex.flexFetch(path, options)
    const result = await flex.handleResponse(response)
    return flattenProposalPayload(result)
  } catch (error) {
    throw normalizeProposalError(error)
  }
}

/**
 * Service for managing match proposals - creating, responding, and retrieving proposals
 */
class MatchProposalService extends BaseService {
  constructor() {
    super()
    this.endpoint = '/api/1.0/match-proposals/'
  }

  /**
   * Create a new match proposal
   * @param {number} opponentPlayerId - ID of the opponent player
   * @param {string} proposedTime - Proposed match time (ISO string)
   * @param {number} leagueId - ID of the league
   * @param {number} divisionId - ID of the division
   * @param {string} expiresAt - Optional expiration time (ISO string)
   * @param {Object} [options] - Optional metadata for proposal
   * @param {boolean} [options.directProposal] - Flag for direct match proposals
   * @returns {Promise<Object>} Service response object
   */
  async createProposal(
    opponentPlayerId,
    proposedTime,
    leagueId,
    divisionId = null,
    expiresAt = null,
    options = {},
  ) {
    return this.execute(async () => {
      this.validateParams({ opponentPlayerId, proposedTime, leagueId }, [
        'opponentPlayerId',
        'proposedTime',
        'leagueId',
      ])

      const { directProposal = false } = options || {}

      const requestBody = {
        opponent_player_id: opponentPlayerId,
        proposed_time: proposedTime,
        league_id: leagueId,
      }

      if (divisionId) {
        requestBody.division_id = divisionId
      }

      // Explicitly include expires_at even if null (null = no expiration)
      if (expiresAt !== undefined) {
        requestBody.expires_at = expiresAt
      }

      if (directProposal) {
        requestBody.direct_proposal = true
      }

      return await requestMatchProposal(this.endpoint + 'post', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        credentials: 'include',
      })
    }, 'Create match proposal')
  }

  /**
   * Get all received match proposals for the current user
   * Filters out inactive proposals that cannot be acted upon
   * @param {number} leagueId - Optional league ID to filter by
   * @param {number} divisionId - Optional division ID to filter by
   * @returns {Promise<Object>} Service response object with proposals data
   */
  async getReceivedProposals(leagueId = null, divisionId = null) {
    return this.execute(async () => {
      // Build query parameters
      const params = new URLSearchParams()
      if (leagueId) params.append('league_id', leagueId)
      if (divisionId) params.append('division_id', divisionId)

      const queryString = params.toString()
      const url = this.endpoint + 'get' + (queryString ? `?${queryString}` : '')

      const result = await requestMatchProposal(url, {
        method: 'GET',
        credentials: 'include',
      })

      // The API returns either the legacy flat payload or a strict envelope.
      // Normalize both to the historical proposals array contract.
      // We normalize this to return just the proposals array as data
      const allProposals = result.proposals || []

      // Filter out inactive proposals - only show proposals that can be acted upon
      // This includes pending proposals that haven't expired or been dismissed
      const activeProposals = allProposals.filter(
        (proposal) => !INACTIVE_PROPOSAL_STATUSES.includes(proposal.status),
      )

      return activeProposals
    }, 'Get received proposals')
  }

  /**
   * Respond to a match proposal (accept/decline)
   * @param {number} proposalId - ID of the proposal to respond to
   * @param {string} response - Response ('accepted' or 'declined')
   * @returns {Promise<Object>} Service response object
   */
  async respondToProposal(proposalId, response) {
    return this.execute(
      async () => {
        this.validateParams({ proposalId, response }, [
          'proposalId',
          'response',
        ])

        if (!['accepted', 'declined'].includes(response)) {
          throw new Error('Response must be either "accepted" or "declined"')
        }

        return await requestMatchProposal(this.endpoint + 'respond', {
          method: 'PUT',
          body: JSON.stringify({
            proposal_id: proposalId,
            response: response,
          }),
          credentials: 'include',
        })
      },
      `${response === 'accepted' ? 'Accept' : 'Decline'} match proposal`,
    )
  }

  /**
   * Accept a match proposal
   * @param {number} proposalId - ID of the proposal to accept
   * @returns {Promise<Object>} Service response object
   */
  async acceptProposal(proposalId) {
    return await this.respondToProposal(proposalId, 'accepted')
  }

  /**
   * Reject a match proposal
   * @param {number} proposalId - ID of the proposal to reject
   * @returns {Promise<Object>} Service response object
   */
  async rejectProposal(proposalId) {
    return await this.respondToProposal(proposalId, 'declined')
  }

  /**
   * Cancel a match proposal (for proposals sent by the user)
   * @param {number} proposalId - ID of the proposal to cancel
   * @returns {Promise<Object>} Service response object
   */
  async cancelProposal(proposalId) {
    return this.execute(async () => {
      this.validateParams({ proposalId }, ['proposalId'])

      return await requestMatchProposal(this.endpoint + 'cancel', {
        method: 'DELETE',
        body: JSON.stringify({
          proposal_id: proposalId,
        }),
        credentials: 'include',
      })
    }, 'Cancel match proposal')
  }

  /**
   * Dismiss a declined match proposal (marks it as acknowledged so it won't show on calendar)
   * @param {number} proposalId - ID of the declined proposal to dismiss
   * @returns {Promise<Object>} Service response object
   */
  async dismissProposal(proposalId) {
    return this.execute(async () => {
      this.validateParams({ proposalId }, ['proposalId'])

      return await requestMatchProposal(this.endpoint + 'dismiss', {
        method: 'PUT',
        body: JSON.stringify({
          proposal_id: proposalId,
        }),
        credentials: 'include',
      })
    }, 'Dismiss proposal')
  }

  /**
   * Process multiple proposals at once (bulk operations)
   * @param {Array} proposals - Array of proposals with their responses
   * @returns {Promise<Object>} Service response object with results
   */
  async processBulkResponses(proposals) {
    return this.execute(async () => {
      const results = []
      const errors = []

      const responses = await Promise.all(
        proposals.map((proposal) =>
          this.respondToProposal(proposal.id, proposal.response)
            .then((res) => ({ id: proposal.id, res }))
            .catch((error) => ({ id: proposal.id, error })),
        ),
      )

      responses.forEach(({ id, res, error }) => {
        if (error) {
          errors.push({ id, error: error.message })
        } else if (res.success) {
          results.push({ id, success: true, data: res.data })
        } else {
          errors.push({ id, error: res.error })
        }
      })

      return {
        processed: results.length,
        errors: errors.length,
        results,
        errorDetails: errors,
      }
    }, 'Process bulk proposal responses')
  }

  /**
   * Get proposal statistics for dashboard display
   * @returns {Promise<Object>} Service response object with statistics
   */
  async getProposalStats() {
    return this.execute(async () => {
      const proposalsResponse = await this.getReceivedProposals()

      if (!proposalsResponse.success) {
        throw new Error('Failed to get proposals for statistics')
      }

      const proposals = proposalsResponse.data || []
      const stats = {
        total: proposals.length,
        pending: proposals.filter((p) => p.status === 'pending').length,
        accepted: proposals.filter((p) => p.status === 'accepted').length,
        declined: proposals.filter((p) => p.status === 'declined').length,
      }

      return stats
    }, 'Get proposal statistics')
  }
}

// Export singleton instance
const matchProposalService = new MatchProposalService()
export default matchProposalService
